// src/pages/lkp/EntityEditor.tsx
// Composes one entity's editor from the layer components. Kept thin on purpose.
import React from "react";
import type { LookupEntity, Buckets } from "../api/lkpEntityAPI";
import { Section, STATUS_META, valueOverlap, type EntityAct } from "./entityShared";
import { BoundFieldsSection, MergeControl, SplitControl } from "./EntityBindings";
import type { BindingCert } from "./LookupEntityWorkspace";
import { CrosswalkSection } from "./EntityCrosswalk";

export default function EntityEditor({ entity: e, buckets, busy, act, allEntities, proposals, cert, onGoStep, onPromote, onPromoteApproved }: {
  entity: LookupEntity; buckets: Buckets | null; busy: string | null;
  act: EntityAct; allEntities: LookupEntity[]; proposals: any[];
  cert?: Map<string, BindingCert>; onGoStep?: (n: 1 | 2) => void;
  onPromote?: (code: string, promote: boolean) => void;
  onPromoteApproved?: () => void;
}) {
  const sm = STATUS_META[e.status] || STATUS_META.discovered;
  const myProps = proposals.filter((p) => p.bound_entity_id === e.entity_id);

  return (
    <>
      <div className="ed-head">
        <div>
          <div className="ed-name">{e.name} <span className="ed-status" style={{ background: sm.bg, color: sm.c }}>{sm.t}</span></div>
          <div className="ed-id">{e.entity_id}{e.description ? ` · ${e.description}` : ""}</div>
        </div>
        <div className="ed-actions">
          {e.review?.status !== "ACCEPTED" && <button className="btn sm" disabled={!!busy} onClick={() => act("accept")}>Accept entity</button>}
          <button className="btn line" disabled={!!busy} onClick={() => { const n = prompt("Rename entity", e.name); if (n) act("rename", { name: n }); }}>Rename</button>
          <MergeControl e={e} all={allEntities} act={act} busy={!!busy} />
           </div>
      </div>

      {e.flags?.split_suggested && (
        <div className="banner warn">
          Bound fields have divergent value sets — they may be different domains.
          <SplitControl e={e} act={act} busy={!!busy} />
        </div>
      )}
      {myProps.length > 0 && (
        <div className="banner info">
          Client mapping differs from this entity for {myProps.map((p) => p.field_id).join(", ")} (client says “{myProps[0].client_logical_field}”). Confirm the binding or remap in Bound fields below.
        </div>
      )}

      <BoundFieldsSection entity={e} busy={busy} act={act} cert={cert} onGoStep={onGoStep} />

      <Section title={`Source evidence (${e.source_values.length})`}
        hint="What's in the DATA (codes, no descriptions) vs what the CLIENT Excel governs. Evidence only — canonical is decided below.">
        {(() => {
          const dataVals = e.source_values.filter((sv) => sv.origin !== "client");
          const clientVals = e.source_values.filter((sv) => sv.origin === "client");
          const v = valueOverlap(e); const nc = (x: any) => String(x ?? "").trim().toLowerCase();
          return (
            <div className="se-groups">
              {v.comparable && (
                <div className="se-sum">
                  <span className="se-b ok">≡ {v.match} match</span>
                  <span className="se-b cli">+{v.clientOnly} client-only (client has, data doesn't)</span>
                  <span className="se-b dat">+{v.profileOnly} data-only (data has, client doesn't)</span>
                </div>
              )}
              <div className="se-grp">
                <div className="se-h">In data (profiling) · {dataVals.length} <span>codes as observed — data-only marked</span></div>
                <div className="chips">
                  {dataVals.slice(0, 60).map((sv) => {
                    const dataOnly = v.cli > 0 && !v.cliSet.has(nc(sv.code));
                    return <span key={"d" + sv.code} className={"chip-v pf" + (dataOnly ? " v-dat" : v.cli > 0 ? " v-match" : "")} title={dataOnly ? "in data, not in client" : "also in client"}><b>{sv.code}</b></span>;
                  })}
                  {dataVals.length > 60 && <span className="muted">+{dataVals.length - 60} more</span>}
                  {dataVals.length === 0 && <span className="muted">no data values captured</span>}
                </div>
              </div>
              <div className="se-grp">
                <div className="se-h">From client Excel · {clientVals.length} <span>client-only marked</span></div>
                <div className="chips">
                  {clientVals.slice(0, 60).map((sv) => {
                    const clientOnly = v.prof > 0 && !v.profSet.has(nc(sv.code));
                    return <span key={"c" + sv.code} className={"chip-v cl" + (clientOnly ? " v-cli" : "")} title={clientOnly ? "client governs this; not seen in data" : "also in data"}><b>{sv.code}</b>{sv.display ? ` ${sv.display}` : ""}</span>;
                  })}
                  {clientVals.length > 60 && <span className="muted">+{clientVals.length - 60} more</span>}
                  {clientVals.length === 0 && <span className="muted">no client values folded — Step 2 optional</span>}
                </div>
              </div>
              <style>{`
                .se-sum{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:4px}
                .se-b{font-size:11px;font-weight:600;padding:3px 9px;border-radius:6px}
                .se-b.ok{background:var(--gov-bg);color:var(--gov)} .se-b.cli{background:var(--wait-bg);color:var(--wait)} .se-b.dat{background:var(--warn-bg);color:var(--warn)}
                .chip-v.v-dat{background:var(--warn-bg);border-color:#e6b5aa;color:var(--warn)}
                .chip-v.v-cli{background:var(--wait-bg);border-color:#e6cfa0;color:var(--wait)}
                .chip-v.v-match b{color:var(--gov)}
              `}</style>
            </div>
          );
        })()}
      </Section>

      <CrosswalkSection entity={e} buckets={buckets} busy={busy} act={act} />
    </>
  );
}
